import { useInfiniteQuery, useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export type TipoAtivacao = 'esim_real' | 'esim_simcard';
export type EstadoAtivacao = 'pendente' | 'em_andamento' | 'validado';
export interface PedidoAtivacao { id: string; numero: string; estado: EstadoAtivacao }
export interface LinhaAtivacao {
  retorno_em?: string | null;
  aguardando_chip_em?: string | null;
  aguardando_chip_por?: string | null;
  situacao?: string | null;
  situacao_rotulo?: string | null;
  /** O ÚLTIMO recado que o cliente recebeu desta linha — a mesma coluna que a
   *  triagem mostra. É a prova de que a automação falou com ele, e quando. */
  aviso_finalidade?: string | null; aviso_status?: string | null; aviso_em?: string | null; aviso_rotulo?: string | null;
  atendimento_em?: string | null;
  /** A etapa com o nome da Visão Prime (nao_iniciada, esperando_cliente…). */
  etapa?: string | null;
  /** Desde quando espera (marco do estado) e se passou do prazo de 24h. */
  espera_desde?: string | null; atrasada?: boolean;
  foto_recebida_em?: string | null;
  status_comercial?: string | null;
  /** Mês de trabalho do item (YYYY-MM). É por ele que se prioriza o atrasado. */
  competencia?: string | null;
  operadora?: string | null; operadora_consultada_em?: string | null;
  id: string; item_id: string; pedido_id: string; ordem: number; telefone: string;
  tipo: TipoAtivacao | null; revisao: number; foto_mensagem_id: string | null;
  foto_url: string | null; iccid: string | null; conferido_em: string | null;
  validada_em: string | null; revisao_validada: number | null; entrada_em: string | null;
  /** Como a linha foi validada: 'master' é exceção lançada por gente, sem
   *  prova do cliente — não prova que a validação do pedido começou. */
  validacao_origem?: string | null;
  confirmacao_status?: string | null; confirmacao_enviada_em?: string | null;
  /** Hora da RESERVA da pergunta, antes de ela sair. Diz se um 'enviando' ainda
   *  está em voo ou se ficou órfão de um processo que caiu no meio do envio. */
  confirmacao_criado_em?: string | null;
  /** Hora do pedido de troca: a pergunta feita depois dela é a vez do cliente. */
  troca_criado_em?: string | null;
  troca_status?: string | null; troca_enviada_em?: string | null; previsao_em?: string | null;
}
/** Foto do chip que já foi lida, com os ICCIDs que saíram dela. Chega pronta do
 * servidor: o modal mostra sem mandar ler de novo (Douglas 22/09/2026). */
export interface ChipLidoDaFoto {
  mensagem_id: string; chips: ChipLido[]; tentativas: number;
  encerrada: boolean; criado_em: string; foto_url: string | null;
}
export interface AtivacaoPedido {
  podeValidarManualmente: boolean;
  chipsLidos?: ChipLidoDaFoto[];
  pedidoIds: string[]; linhas: LinhaAtivacao[];
  passagem: { liberado: boolean; motivo: string | null; total: number; validadas: number };
}
export interface ChipLido { iccid: string; linhaAnotada: string | null }
export interface LeituraChip {
  tipo: 'chip' | 'nao_chip' | 'ilegivel'; iccid: string | null; revisao: number; mensagemId: string; exigeConferencia: boolean;
  /** O cliente manda os chips todos numa foto só. Com mais de um, iccid vem
   * null de propósito: quem diz qual é o desta linha é a pessoa. */
  chips: ChipLido[];
  /** Números que uma leitura viu e a outra não: a foto não permite confirmar. */
  semAcordo?: string[];
  /** Números que não fecham no dígito verificador do chip. */
  reprovadosPeloDigito?: string[];
}

export async function requisitarAtivacao<T>(path: string, body?: unknown): Promise<T> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error('Sua sessão expirou. Entre novamente antes de continuar.');
  const base = ((import.meta.env.VITE_SUPABASE_URL as string) || 'https://beta.crm-api.primetecia.com.br/api').replace(/\/$/, '');
  let r: Response;
  try { r = await fetch(`${base}/v1/qualidade/ativacao/${path}`, {
    method: body === undefined ? 'GET' : 'POST',
    headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  }); } catch { throw new Error('Não foi possível conectar ao CRM. Tente novamente para carregar os pedidos; nenhuma alteração foi confirmada.'); }
  const result = await r.json().catch(() => ({ error: 'O CRM não respondeu corretamente. Tente carregar novamente.' }));
  if (!r.ok) throw new Error(result.error || 'Não foi possível carregar a conferência de eSIM. Tente novamente.');
  return result.data as T;
}

export function usePedidosAtivacao(conversaId: string, aberto: boolean) {
  return useQuery({ queryKey: ['ativacao-pedidos-conversa', conversaId], enabled: aberto && !!conversaId,
    staleTime: 15_000,
    queryFn: () => requisitarAtivacao<PedidoAtivacao[]>(`conversas/${conversaId}/pedidos`) });
}
export function useAtivacaoPedido(pedidoId: string, aberto: boolean) {
  return useQuery({ queryKey: ['ativacao-pedido', pedidoId], enabled: aberto && !!pedidoId,
    refetchInterval: aberto ? 10_000 : false,
    queryFn: () => requisitarAtivacao<AtivacaoPedido>(`pedidos/${pedidoId}`) });
}
export function useFamiliasAtivacao(pedidos: Array<{ id: string; numero: string; estado?: EstadoAtivacao }>, aberto: boolean, buscar = aberto) {
  return useQueries({ queries: pedidos.map(p => ({ queryKey: ['ativacao-pedido', p.id], enabled: buscar,
    refetchInterval: aberto ? 10_000 : false,
    queryFn: () => requisitarAtivacao<AtivacaoPedido>(`pedidos/${p.id}`) })) });
}
interface FotoChipConversa { id: string; media_url: string | null; created_at: string }
export function useFotosChipConversa(conversaId: string, enabled: boolean) {
  return useInfiniteQuery({ queryKey: ['ativacao-fotos-conversa', conversaId], enabled, initialPageParam: 0,
    queryFn: async ({ pageParam }): Promise<FotoChipConversa[]> => {
      const { data, error } = await supabase.from('whatsapp_messages').select('id,media_url,created_at')
        .eq('conversation_id', conversaId).eq('direction', 'inbound').eq('type', 'image')
        .is('deleted_at', null).or('is_private_note.is.null,is_private_note.eq.false').not('media_url', 'is', null)
        .order('created_at', { ascending: false }).order('id', { ascending: false }).range(pageParam, pageParam + 23)
        .returns<FotoChipConversa[]>();
      if (error) throw error;
      return data || [];
    }, getNextPageParam: (last, pages) => last.length === 24 ? pages.length * 24 : undefined });
}
export function useAcaoAtivacao(_pedidoId: string) {
  const qc = useQueryClient();
  return useMutation({ mutationFn: ({ path, body }: { path: string; body: unknown }) => requisitarAtivacao(path, body),
    onSettled: () => {
      const imediatas = ['ativacao-pedido', 'ativacao-retornos-troca', 'ativacao-pedidos-conversa'];
      for (const key of imediatas) void qc.invalidateQueries({ queryKey: [key] });
      for (const key of ['crm-panel-pedidos', 'kanban-optimized', 'triagem-fluxo', 'triagem-esim', 'qualidade-inbox', 'whatsapp-conversations', 'comissao-bundle-visao']) {
        void qc.invalidateQueries({ queryKey: [key] });
      }
    } });
}
export function useLerFotoChip() {
  return useMutation({ mutationFn: ({ linhaId, revisao }: { linhaId: string; revisao: number }) =>
    requisitarAtivacao<LeituraChip>(`linhas/${linhaId}/ler-foto`, { revisao }) });
}
